import React, { useEffect, useMemo, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import ArrowRightIcon from '@mui/icons-material/ChevronRightRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { ArrowRightLeft } from 'lucide-react';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import Menu from '@mui/material/Menu';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';

import { LEAD_STAGES } from 'src/services/api';
import { Flex, FlexColumn } from 'src/components/flex';
import { LeadsIcon, PropertiesIcon, UsersIcon } from 'src/icons';
import { StageChip } from 'src/components/input/select-lead-stage/SelectLeadStage';
import SelectLabels from 'src/components/input/select-labels/SelectLabels';
import AutocompleteMultiple from 'src/components/input/autocomplete-multiple/AutocompleteMultiple';
import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import useTeamMembers from 'src/services/api/team/members/use-team-members';
import { type TConversationsFilter } from 'src/services/api/messages/use-conversations-search';
import { getAllowedValuesForKey } from 'src/pages/landlord/conversations/utils/filter-narrowing';

export enum FilteringBy {
    Assignee = 'Assignee',
    IngestionSource = 'Ingestion Source',
    LeadStage = 'Lead Stage',
    LeadLabel = 'Lead Label',
    Property = 'Property',
    PropertyLabel = 'Property Label',
    MessageDirection = 'Last Message Sender',
}

const teamMemberFilters = [FilteringBy.Assignee];
const ingestionFilters = [FilteringBy.IngestionSource];
const leadFilters = [FilteringBy.LeadStage, FilteringBy.LeadLabel];
const propertyFilters = [FilteringBy.Property, FilteringBy.PropertyLabel];
const conversationFilters = [FilteringBy.MessageDirection];

type Props = {
    anchorEl?: HTMLElement | null;
    isFilterOpen: boolean;
    setFilterOpen: (open: boolean) => void;
    filter: TConversationsFilter;
    inboxFilters?: TConversationsFilter;
    properties: Array<any>;
    setFilter: (newFilter: Partial<TConversationsFilter>) => void;
    resetFilter: () => void;
    disabledFilters?: FilteringBy[];
}

export default function ConversationsFilterModal({ anchorEl, isFilterOpen, setFilterOpen, filter, inboxFilters, properties, setFilter, resetFilter, disabledFilters = [] }: Props) {
    const { leadIngestionSources } = useAuthenticatedState();
    const { data: teamMembersWithPhotos } = useTeamMembers();

    const searchRef = useRef<HTMLInputElement>(null);

    const [filteringBy, setFilteringBy] = useState<FilteringBy>();

    const handleClose = () => {
        setFilterOpen(false);
        setFilteringBy(undefined);
    };

    // Focus on the search input when the modal is opened
    useEffect(() => {
        if (isFilterOpen) {
            setTimeout(() => searchRef.current?.focus(), 0);
        }
    }, [isFilterOpen]);

    // When the inbox constrains a dimension, the picker may only offer values within the
    // inbox's set (so the temporary filter can only narrow the view). `undefined` means the
    // inbox does not constrain that dimension and every value is allowed.
    const allowedAssigneeIds = getAllowedValuesForKey(inboxFilters, 'assigneeIds');
    const allowedIngestionSources = getAllowedValuesForKey(inboxFilters, 'ingestionSources');
    const allowedLeadStages = getAllowedValuesForKey(inboxFilters, 'leadStages');
    const allowedLeadLabelIds = getAllowedValuesForKey(inboxFilters, 'leadLabelIds');
    const allowedPropertyIds = getAllowedValuesForKey(inboxFilters, 'propertyIds');
    const allowedPropertyLabelIds = getAllowedValuesForKey(inboxFilters, 'propertyLabelIds');
    const isDirectionPinned = inboxFilters?.lastMessageDirection !== undefined;

    // Labels the inbox already excludes/includes constrain what the user may pick: a label the
    // inbox marks "is not" cannot be offered under "is" (and vice versa), since the user can only
    // narrow within the inbox, never contradict it. Combine the inbox's sets with the user's so
    // each picker hides what the other side already holds — at the inbox level or the user level.
    const excludedLeadLabelIds = [
        ...(inboxFilters?.excludeLeadLabelIds ?? []),
        ...(filter.excludeLeadLabelIds ?? []),
    ].map(String);
    const includedLeadLabelIds = [
        ...(inboxFilters?.leadLabelIds ?? []),
        ...(filter.leadLabelIds ?? []),
    ].map(String);

    const teamMemberOptions = useMemo(() => {
        const allowed = allowedAssigneeIds ? new Set(allowedAssigneeIds) : undefined;
        return (teamMembersWithPhotos || [])
        // When allowed is undefined, it means we should keep all filter options
            .filter(member => !allowed || allowed.has(String(member.id)))
            .map(member => ({
                value: String(member.id),
                label: member.name,
            }));
    }, [teamMembersWithPhotos, allowedAssigneeIds]);

    const ingestionSourceOptions = useMemo(() => {
        const sources = allowedIngestionSources ?? leadIngestionSources;
        return sources.map(source => ({
            value: source,
            label: source,
        }));
    }, [leadIngestionSources, allowedIngestionSources]);

    const propertyOptions = useMemo(() => {
        const allowed = allowedPropertyIds ? new Set(allowedPropertyIds) : undefined;
        const _propertyOptions = (properties || [])
            .filter(property => !allowed || allowed.has(String(property.id)))
            .map(property => ({
                value: String(property.id),
                label: property.name,
            }));

        return allowed ?
            _propertyOptions :
            [{ value: 'none', label: 'None' }, ..._propertyOptions];
    }, [properties, allowedPropertyIds]);

    const visibleLeadStages = useMemo(() => {
        const allowed = allowedLeadStages ? new Set(allowedLeadStages) : undefined;
        return LEAD_STAGES.filter(stage => !allowed || allowed.has(stage));
    }, [allowedLeadStages]);

    return (
        <Menu
            onClose={handleClose}
            open={isFilterOpen && !!anchorEl}
            anchorEl={anchorEl}
            slotProps={{
                root: {
                    sx: {
                        '@media (max-width: 900px)': {
                            p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center'
                        }
                    }
                },
                paper: {
                    sx: {
                        width: 400,
                        maxWidth: '100%',
                        '@media (max-width: 900px)': {
                            position: 'static', width: 500, maxWidth: '100%'
                        }
                    }
                }
            }}
        >
            <Flex alignItems="center" p={2}>
                {!!filteringBy && (
                    <IconButton onClick={() => setFilteringBy(undefined)} edge="start">
                        <ArrowBackRounded />
                    </IconButton>
                )}

                <Typography>
                    Filter by
                </Typography>
                <Typography>
                    {filteringBy}
                </Typography>
            </Flex>

            {!filteringBy && (
                <div>
                    {teamMemberFilters.filter(i => !disabledFilters.includes(i)).map((item) => (
                        <MenuItem key={item} onClick={() => setFilteringBy(item)}>
                            <ListItemIcon>
                                <UsersIcon />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography>
                                    {item}
                                </Typography>
                            </ListItemText>
                            <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                                <ArrowRightIcon />
                            </ListItemIcon>
                        </MenuItem>
                    ))}

                    <Divider />

                    {ingestionFilters.filter(i => !disabledFilters.includes(i)).map((item) => (
                        <MenuItem key={item} onClick={() => setFilteringBy(item)}>
                            <ListItemIcon>
                                <DownloadRoundedIcon />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography>
                                    {item}
                                </Typography>
                            </ListItemText>
                            <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                                <ArrowRightIcon />
                            </ListItemIcon>
                        </MenuItem>
                    ))}

                    <Divider />

                    {leadFilters.filter(i => !disabledFilters.includes(i)).map((item) => (
                        <MenuItem key={item} onClick={() => setFilteringBy(item)}>
                            <ListItemIcon>
                                <LeadsIcon />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography>
                                    {item}
                                </Typography>
                            </ListItemText>
                            <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                                <ArrowRightIcon />
                            </ListItemIcon>
                        </MenuItem>
                    ))}

                    <Divider />

                    {propertyFilters.filter(i => !disabledFilters.includes(i)).map((item) => (
                        <MenuItem key={item} onClick={() => setFilteringBy(item)}>
                            <ListItemIcon>
                                <PropertiesIcon />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography>
                                    {item}
                                </Typography>
                            </ListItemText>
                            <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                                <ArrowRightIcon />
                            </ListItemIcon>
                        </MenuItem>
                    ))}

                    <Divider />

                    {conversationFilters.filter(i => !disabledFilters.includes(i)).map((item) => (
                        <MenuItem key={item} onClick={() => setFilteringBy(item)}>
                            <ListItemIcon>
                                <ArrowRightLeft size={20} />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography>
                                    {item}
                                </Typography>
                            </ListItemText>
                            <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                                <ArrowRightIcon />
                            </ListItemIcon>
                        </MenuItem>
                    ))}
                </div>
            )}

            {!!filteringBy && (
                <div>
                    {filteringBy === FilteringBy.Assignee && (
                        <Flex p={1}>
                            <AutocompleteMultiple
                                placeholder="Select Assignees"
                                options={teamMemberOptions}
                                value={filter.assigneeIds}
                                onChange={(assigneeIds) => {
                                    setFilter({
                                        assigneeIds: assigneeIds || [],
                                    });
                                }}
                                size="small"
                                fullWidth
                                onKeyDown={(event) => {
                                    // Prevent keyboard navigation from moving focus away
                                    if (event.key === 'f' || event.key === 'F') {
                                        event.stopPropagation();
                                    }
                                }}
                                renderOption={(optionProps, option) => {
                                    const member = teamMembersWithPhotos?.find(m => String(m.id) === option.value);
                                    return (
                                        <li {...optionProps} key={option.value}>
                                            <Flex alignItems="center" gap={1} overflow="hidden">
                                                <ProfileImageRounded url={member?.profile_photo_url} width={24} height={24} showEmptyImage userName={member?.name} />
                                                <span className="ellipsisText" style={{ display: 'block', minWidth: 0 }}>
                                                    {option.label}
                                                </span>
                                            </Flex>
                                        </li>
                                    );
                                }}
                            />
                        </Flex>
                    )}

                    {filteringBy === FilteringBy.IngestionSource && (
                        <Flex p={1}>
                            <AutocompleteMultiple
                                placeholder="Select Ingestion Sources"
                                options={ingestionSourceOptions}
                                value={filter.ingestionSources}
                                onChange={(ingestionSources) => {
                                    setFilter({
                                        ingestionSources: ingestionSources || [],
                                    });
                                }}
                                size="small"
                                fullWidth
                                data-testid="filter-by-ingestion-source"
                                renderOption={(optionProps, option) => (
                                    <li {...optionProps} key={option.value}>
                                        <span className="ellipsisText" style={{ display: 'block', width: '100%' }}>
                                            {option.label}
                                        </span>
                                    </li>
                                )}
                                onKeyDown={(event) => {
                                    // Prevent keyboard navigation from moving focus away
                                    if (event.key === 'f' || event.key === 'F') {
                                        event.stopPropagation();
                                    }
                                }}
                            />
                        </Flex>
                    )}

                    {filteringBy === FilteringBy.LeadStage && visibleLeadStages.map(stage => {
                        const isChecked = (filter.leadStages ?? []).includes(stage);
                        const onToggleStage = () => {
                            const current = filter.leadStages ?? [];
                            setFilter({
                                leadStages: isChecked ?
                                    current.filter(s => s !== stage) :
                                    [...current, stage],
                            });
                        };
                        return (
                            <MenuItem key={stage} onClick={onToggleStage}>
                                <Flex alignItems="center">
                                    <Checkbox
                                        checked={isChecked}
                                        sx={{ padding: 0, paddingLeft: 1 }}
                                        data-testid={`lead-stage-filter-${stage.replace(/_/g, '-')}`}
                                    />
                                    <StageChip value={stage} />
                                </Flex>
                            </MenuItem>
                        );
                    })}

                    {filteringBy === FilteringBy.LeadLabel && (
                        <Flex p={1} flexDirection="column" gap={2}>
                            <SelectLabels
                                type="lead"
                                label="Lead label is"
                                placeholder="Select lead labels..."
                                allowedValues={allowedLeadLabelIds}
                                excludeValues={excludedLeadLabelIds}
                                value={filter.leadLabelIds}
                                onChange={(value) => {
                                    const excludeLeadLabelIds = filter.excludeLeadLabelIds ? filter.excludeLeadLabelIds.filter(id => !value?.includes(id)) : [];
                                    setFilter({
                                        leadLabelIds: value || [],
                                        excludeLeadLabelIds,
                                    });
                                }}
                                size="small"
                                fullWidth
                                onKeyDown={(event) => {
                                    // Prevent keyboard navigation from moving focus away
                                    if (event.key === 'f' || event.key === 'F') {
                                        event.stopPropagation();
                                    }
                                }}
                            />

                            <SelectLabels
                                type="lead"
                                label="Lead label is not"
                                placeholder="Select lead labels..."
                                excludeValues={includedLeadLabelIds}
                                value={filter.excludeLeadLabelIds}
                                onChange={(value) => {
                                    const leadLabelIds = filter.leadLabelIds ? filter.leadLabelIds.filter(id => !value?.includes(id)) : [];
                                    setFilter({
                                        leadLabelIds,
                                        excludeLeadLabelIds: value || [],
                                    });
                                }}
                                size="small"
                                fullWidth
                                onKeyDown={(event) => {
                                    // Prevent keyboard navigation from moving focus away
                                    if (event.key === 'f' || event.key === 'F') {
                                        event.stopPropagation();
                                    }
                                }}
                            />
                        </Flex>
                    )}

                    {filteringBy === FilteringBy.Property && (
                        <Flex p={1}>
                            <AutocompleteMultiple
                                placeholder="Select properties"
                                options={propertyOptions}
                                value={filter.propertyIds}
                                onChange={(propertyIds) => {
                                    if (propertyIds?.includes('none')) {
                                        setFilter({
                                            propertyIds: [],
                                            hasNoProperty: true,
                                        });

                                        handleClose();
                                    } else {
                                        setFilter({
                                            propertyIds: propertyIds || [],
                                            hasNoProperty: false,
                                        });
                                    }
                                }}
                                size="small"
                                fullWidth
                                onKeyDown={(event) => {
                                    // Prevent keyboard navigation from moving focus away
                                    if (event.key === 'f' || event.key === 'F') {
                                        event.stopPropagation();
                                    }
                                }}
                                renderOption={(optionProps, option) => (
                                    <li {...optionProps} key={option.value}>
                                        <span className="ellipsisText" style={{ display: 'block', width: '100%' }}>
                                            {option.label}
                                        </span>
                                    </li>
                                )}
                            />
                        </Flex>
                    )}

                    {filteringBy === FilteringBy.MessageDirection && (
                        <Flex px={2} py={1}>
                            <RadioGroup
                                value={(isDirectionPinned ? inboxFilters?.lastMessageDirection : filter.lastMessageDirection) ?? 'undefined'}
                                onChange={(_, value) => setFilter({ lastMessageDirection: value === 'undefined' ? undefined : value as 'in' | 'out' })}
                            >
                                <FlexColumn rowGap={0}>
                                    <FormControlLabel value="undefined" label="Any" control={<Radio />} disabled={isDirectionPinned} />
                                    <FormControlLabel value="in" label="Lead" control={<Radio />} disabled={isDirectionPinned} />
                                    <FormControlLabel value="out" label="Us" control={<Radio />} disabled={isDirectionPinned} />
                                </FlexColumn>
                            </RadioGroup>
                        </Flex>
                    )}

                    {filteringBy === FilteringBy.PropertyLabel && (
                        <Flex p={1}>
                            <SelectLabels
                                type="property"
                                placeholder="Select property labels..."
                                allowedValues={allowedPropertyLabelIds}
                                value={filter.propertyLabelIds}
                                onChange={(propertyLabelIds) => setFilter({ propertyLabelIds: propertyLabelIds || [] })}
                                size="small"
                                fullWidth
                                onKeyDown={(event) => {
                                    // Prevent keyboard navigation from moving focus away
                                    if (event.key === 'f' || event.key === 'F') {
                                        event.stopPropagation();
                                    }
                                }}
                            />
                        </Flex>
                    )}
                </div>
            )}

            <Divider />

            <Flex width="100%" p={1} justifyContent="space-between">
                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                        resetFilter();
                        handleClose();
                    }}
                    size="small"
                    fullWidth
                >
                    Clear All
                </Button>
                <Button
                    variant="outlined"
                    color="dark"
                    onClick={handleClose}
                    size="small"
                    fullWidth
                >
                    Close
                </Button>
            </Flex>
        </Menu>
    );
}
